import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { MarketingEmailComposer } from '../../../modules/timeline/components/marketing-email-composer.component';
import { useLeadsApi, Lead } from '../../../common/hooks/use-leads-api.hook';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { LimitReachedGate } from '../../../components/LimitReachedGate';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../../common/components/PageContainer';

export const ComposeEmailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const leadsAPI = useLeadsApi();

  const [leadData, setLeadData] = useState<Lead | undefined>();
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>('');

  // Monthly email quota — block composing when reached (proactive paywall).
  const plansApi = usePlansApi();
  const [emailUsage, setEmailUsage] = useState<{ current: number; limit: number } | null>(null);
  useEffect(() => {
    let active = true;
    plansApi.getMyPlan()
      .then((r: any) => { if (active) setEmailUsage(r?.usage?.maxEmailsPerMonth ?? null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const emailLimitReached =
    !!emailUsage && emailUsage.limit !== -1 && emailUsage.limit != null && emailUsage.current >= emailUsage.limit;

  const emailParam = searchParams.get('email') || '';

  // Load all leads for dropdown
  useEffect(() => {
    const loadAllLeads = async () => {
      setIsLoadingLeads(true);
      try {
        const response = await leadsAPI.findAllLeads({
          limit: 100
        });
        setAllLeads(response.data || []);

        // Only set selected email if it exists in URL params
        if (emailParam) {
          setSelectedEmail(emailParam);
        }
      } catch (error) {
        console.error('Error loading leads:', error);
      } finally {
        setIsLoadingLeads(false);
      }
    };

    loadAllLeads();
  }, []);

  // Load specific lead data when email is selected
  useEffect(() => {
    const loadLeadData = async () => {
      if (!selectedEmail) {
        setLeadData(undefined);
        return;
      }

      setIsLoadingLead(true);
      try {
        const response = await leadsAPI.findAllLeads({
          limit: 1,
          email: selectedEmail
        });

        if (response.data && response.data.length > 0) {
          setLeadData(response.data[0]);
        }
      } catch (error) {
        console.error('Error loading lead data:', error);
      } finally {
        setIsLoadingLead(false);
      }
    };

    loadLeadData();
  }, [selectedEmail]);

  const handleLeadSelect = (email: string) => {
    setSelectedEmail(email);
    setSearchParams({ email });
  };

  const handleClose = () => {
    navigate(-1);
  };

  // Always show the composer with lead selector integrated
  return (
    <PageContainer
      requiresFeature="emailAutomation"
      breadcrumbs={[
        { label: t('SIDEBAR.MARKETING.TITLE') },
        { label: t('SIDEBAR.MARKETING.EMAIL_MARKETING') },
        { label: t('SIDEBAR.MARKETING.COMPOSE_EMAIL') }
      ]}
    >
      {/* The composer owns its own scrolling, so the page gives it the viewport
          minus the navbar and pulls the container's bottom margin back. */}
      <div className="mt-1 -mb-2 flex h-[calc(100vh-130px)] flex-col">
        {isLoadingLeads ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <LimitReachedGate
            reached={emailLimitReached}
            title={t('PLANS.EMAIL_LIMIT.TITLE', 'Monthly email limit reached')}
            description={t(
              'PLANS.EMAIL_LIMIT.DESCRIPTION',
              "You've used all {{limit}} emails in your plan this billing period. Upgrade to send more.",
              { limit: emailUsage?.limit ?? 0 },
            )}
          >
            <MarketingEmailComposer
              onClose={handleClose}
              leadEmail={selectedEmail}
              leadEvents={[]}
              leadData={leadData}
              allLeads={allLeads}
              onLeadSelect={handleLeadSelect}
              isLoadingLead={isLoadingLead}
            />
          </LimitReachedGate>
        )}
      </div>
    </PageContainer>
  );
};

export default ComposeEmailPage;
